// ─────────────────────────────────────────────────────────────────────────────
// WEB-ONLY OVERRIDE of ./ConfirmationModalWithImage.tsx
//
// Why this file exists:
//   On react-native-web the two footer buttons (नहीं / हाँ) render squished —
//   collapsed to their text width with the label spilling outside the pill.
//   Root cause is an RNW flexbox divergence: `buttonContainer` sets
//   flexDirection:"row" and each `button` uses flex:1, but the container has no
//   explicit width. On native the row stretches to the modal width (width-64)
//   and flex:1 splits it evenly; on RNW the row shrinks to content, so flex:1
//   has almost nothing to divide and the buttons stay tiny.
//
// The fix (web-only): buttonContainer gets `width:"100%"` so flex:1 resolves
//   against the full modal width, exactly like native.
//
// Why an override instead of editing the shared .tsx:
//   The native ConfirmationModalWithImage.tsx is consumed by rn-mobile and is
//   already correct there. Metro (the native bundler) ignores `.web.tsx`, so
//   this file is invisible to mobile — the native bundle is unchanged. Webpack
//   (webpack.web.config.js lists ".web.tsx" first in resolve.extensions) picks
//   this file for the web build only.
//
// KEEP IN SYNC: if the native ./ConfirmationModalWithImage.tsx changes, mirror
//   the change here. The ONLY intended difference is buttonContainer.width.
// ─────────────────────────────────────────────────────────────────────────────
import React, { memo } from "react";
import {
  View,
  Modal,
  TouchableOpacity,
  StyleSheet,
  TouchableWithoutFeedback,
} from "react-native";
import Text from "@components/CustomText";
import color from "@constants/color";
import { width } from "@constants/metrics";

interface Props {
  imageComponent: any;
  text?: string;
  buttonText?: [];
  visible?: boolean;
  setVisible: (value: boolean) => void;
}

const ConfirmationModalWithImage = (props: Props) => {
  const {
    imageComponent = <View />,
    text,
    buttonText,
    visible = true,
    setVisible,
  } = props;

  return (
    <Modal
      statusBarTranslucent
      navigationBarTranslucent
      animationType="fade"
      transparent={true}
      visible={!!visible}
      onRequestClose={() => setVisible(false)}
    >
      <TouchableWithoutFeedback onPress={() => setVisible(false)}>
        <View style={styles.modalBackground} />
      </TouchableWithoutFeedback>
      <View style={styles.modalContent}>
        <View style={styles.imageContainer}>{imageComponent}</View>
        <Text variant="body_text_4" style={styles.subHeadingText}>
          {text}
        </Text>
        <View style={styles.buttonContainer}>
          {buttonText &&
            buttonText.map((item, index) => (
              <TouchableOpacity
                key={`${index}=${item.text}`}
                style={[
                  styles.button,
                  item.type === "submit" && styles.submitButton,
                ]}
                onPress={() => item.onPress && item.onPress()}
                testID={`${index}=${item.text}`}
              >
                <Text
                  variant="body_text_2"
                  style={[
                    styles.btnText,
                    item.type === "submit" && styles.submitbtnText,
                  ]}
                >
                  {item.text}
                </Text>
              </TouchableOpacity>
            ))}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalBackground: {
    flex: 1,
    backgroundColor: color.mediumBlacklightBlack,
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    position: "absolute",
    top: "45%",
    left: "50%",
    transform: [{ translateX: -(width - 64) / 2 }, { translateY: -100 }],
    backgroundColor: color.white,
    width: width - 64,
    borderRadius: 20,
    padding: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  // Unused here, as in the native file — kept so the two stay line-for-line in sync.
  // eslint-disable-next-line react-native/no-unused-styles
  headingText: {
    color: color.Gray20,
    paddingVertical: 5,
  },
  subHeadingText: {
    color: color.mediumBlack,
    textAlign: "center",
    paddingVertical: 5,
    marginTop: 10,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "space-around",
    // WEB-ONLY FIX: force the row to the modal's full width so each flex:1
    // button gets an equal half. Without this, RNW collapses the row to its
    // content width and the buttons render squished. No effect on native
    // (the row already fills the modal there) — and native never loads this file.
    width: "100%",
  },
  button: {
    marginVertical: 12,
    marginHorizontal: 6,
    paddingVertical: 10,
    borderRadius: 8,
    flex: 1,
    alignItems: "center",
    backgroundColor: color.lighterBlack,
  },
  submitButton: {
    backgroundColor: "#198155",
  },
  submitbtnText: {
    color: color.white,
  },
  btnText: {
    color: color.mediumBlack,
  },
  imageContainer: {
    paddingTop: 10,
    paddingBottom: 4,
  },
});

export default memo(ConfirmationModalWithImage);
